import { useEffect, useRef, useState } from 'react';

/**
 * Debug utility to track component renders and re-renders
 * Usage: useDebugRender('ComponentName', { prop1, prop2, state1 });
 */
export function useDebugRender(componentName: string, deps: Record<string, any> = {}, enabled: boolean = true) {
  const renderCount = useRef(0);
  const prevDeps = useRef<Record<string, any>>({});
  const isMounted = useRef(false);
  const wasEnabledRef = useRef(enabled);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Track if enabled state changed
  if (wasEnabledRef.current !== enabled) {
    wasEnabledRef.current = enabled;
    if (enabled) {
      console.log(`[DEBUG-RENDER] ${componentName} - Debug logging just enabled, will log from next render`);
    }
  }

  if (!enabled) {
    return;
  }

  renderCount.current += 1;
  const renderNum = renderCount.current;

  // Log initial render
  if (renderNum === 1) {
    console.log(`[RENDER] ${componentName} - Initial render`, {
      deps,
      timestamp: new Date().toISOString(),
    });
    prevDeps.current = { ...deps };
    return;
  }

  // Find what changed
  const changes: Record<string, { from: any; to: any }> = {};
  const allKeys = new Set([...Object.keys(prevDeps.current), ...Object.keys(deps)]);

  for (const key of allKeys) {
    const prev = prevDeps.current[key];
    const curr = deps[key];
    
    // Deep comparison for objects/arrays
    if (JSON.stringify(prev) !== JSON.stringify(curr)) {
      changes[key] = { from: prev, to: curr };
    }
  }

  if (Object.keys(changes).length > 0) {
    console.log(`[RENDER] ${componentName} - Re-render #${renderNum}`, {
      changes,
      allDeps: deps,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(2, 8).join('\n'),
    });
  } else {
    console.log(`[RENDER] ${componentName} - Re-render #${renderNum} (no deps changed)`, {
      deps,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(2, 8).join('\n'),
    });
  }

  prevDeps.current = { ...deps };
}

/**
 * Debug utility to track effect executions
 */
export function useDebugEffect(
  effectName: string,
  effectFn: () => void | (() => void),
  deps: any[] = [],
  enabled: boolean = true
) {
  const prevDeps = useRef<any[]>([]);
  const effectCount = useRef(0);

  useEffect(() => {
    if (!enabled) {
      return effectFn();
    }
    
    effectCount.current += 1;
    const effectNum = effectCount.current;

    // Find what changed
    const changes: Array<{ index: number; from: any; to: any }> = [];
    deps.forEach((dep, index) => {
      const prev = prevDeps.current[index];
      if (JSON.stringify(prev) !== JSON.stringify(dep)) {
        changes.push({ index, from: prev, to: dep });
      }
    });

    if (changes.length > 0 || effectNum === 1) {
      console.log(`[EFFECT] ${effectName} - Execution #${effectNum}`, {
        changes: changes.length > 0 ? changes : 'initial',
        deps,
        timestamp: new Date().toISOString(),
      });
    }

    prevDeps.current = [...deps];
    return effectFn();
  }, deps);
}

/**
 * Debug utility to track state changes
 */
export function useDebugState<T>(stateName: string, state: T, enabled: boolean = true) {
  const prevState = useRef<T>(state);
  const changeCount = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    
    if (JSON.stringify(prevState.current) !== JSON.stringify(state)) {
      changeCount.current += 1;
      console.log(`[STATE] ${stateName} - Change #${changeCount.current}`, {
        from: prevState.current,
        to: state,
        timestamp: new Date().toISOString(),
      });
      prevState.current = state;
    }
  }, [state, stateName, enabled]);
}

/**
 * Enable/disable debug logging globally
 */
let debugEnabled = typeof window !== 'undefined' && 
  (localStorage.getItem('__DEBUG_RENDER__') === 'true' || 
   new URLSearchParams(window.location.search).has('debug-render'));

export function setDebugRenderEnabled(enabled: boolean) {
  debugEnabled = enabled;
  if (typeof window !== 'undefined') {
    if (enabled) {
      localStorage.setItem('__DEBUG_RENDER__', 'true');
      console.log('[DEBUG] Render debugging ENABLED');
      console.log('[DEBUG] You will see [RENDER], [EFFECT], and [STATE] logs in the console');
    } else {
      localStorage.removeItem('__DEBUG_RENDER__');
      console.log('[DEBUG] Render debugging DISABLED');
    }
  }
}

export function isDebugRenderEnabled() {
  return debugEnabled;
}

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).__DEBUG_RENDER__ = {
    enable: () => {
      setDebugRenderEnabled(true);
      (window as any).__FORCE_DEBUG_RENDER__ = true;
      // Force a re-render by triggering storage event
      window.dispatchEvent(new StorageEvent('storage', { key: '__DEBUG_RENDER__' }));
    },
    disable: () => {
      setDebugRenderEnabled(false);
      (window as any).__FORCE_DEBUG_RENDER__ = false;
      window.dispatchEvent(new StorageEvent('storage', { key: '__DEBUG_RENDER__' }));
    },
    isEnabled: () => checkDebugEnabled(),
  };
}

// Helper to check if debug is enabled
function checkDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('__DEBUG_RENDER__') === 'true' || 
         new URLSearchParams(window.location.search).has('debug-render') ||
         (window as any).__FORCE_DEBUG_RENDER__ === true;
}

// Wrapped versions that check the flag - always call hooks but conditionally log
export function useDebugRenderIf(componentName: string, deps: Record<string, any> = {}) {
  const [enabled, setEnabled] = useState(() => {
    const isEnabled = checkDebugEnabled();
    if (isEnabled) {
      console.log(`[DEBUG-RENDER] ${componentName} - Hook initialized with debug ENABLED`);
    }
    return isEnabled;
  });
  
  // Update enabled state when it might change
  useEffect(() => {
    const updateEnabled = () => {
      const isEnabled = checkDebugEnabled();
      if (isEnabled !== enabled) {
        console.log(`[DEBUG-RENDER] ${componentName} - Debug ${isEnabled ? 'ENABLED' : 'DISABLED'}`);
        setEnabled(isEnabled);
      }
    };
    
    updateEnabled();
    // Check periodically and on storage events
    const interval = setInterval(updateEnabled, 200);
    window.addEventListener('storage', updateEnabled);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', updateEnabled);
    };
  }, [enabled, componentName]);
  
  // Always call useDebugRender, it will check enabled internally
  useDebugRender(componentName, deps, enabled);
}

export function useDebugEffectIf(
  effectName: string,
  effectFn: () => void | (() => void),
  deps: any[] = []
) {
  const [enabled, setEnabled] = useState(() => checkDebugEnabled());
  
  useEffect(() => {
    const updateEnabled = () => {
      const isEnabled = checkDebugEnabled();
      if (isEnabled !== enabled) {
        setEnabled(isEnabled);
      }
    };
    
    updateEnabled();
    const interval = setInterval(updateEnabled, 200);
    window.addEventListener('storage', updateEnabled);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', updateEnabled);
    };
  }, [enabled]);
  
  useDebugEffect(effectName, effectFn, deps, enabled);
}

export function useDebugStateIf<T>(stateName: string, state: T) {
  const [enabled, setEnabled] = useState(() => checkDebugEnabled());
  
  useEffect(() => {
    const updateEnabled = () => {
      const isEnabled = checkDebugEnabled();
      if (isEnabled !== enabled) {
        setEnabled(isEnabled);
      }
    };
    
    updateEnabled();
    const interval = setInterval(updateEnabled, 200);
    window.addEventListener('storage', updateEnabled);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', updateEnabled);
    };
  }, [enabled]);
  
  useDebugState(stateName, state, enabled);
}

